import { FamilyManagementCard } from "./admin/components/FamilyManagementCard";
import { ParentsCard } from "./admin/components/ParentsCard";
import { EnrollmentCard } from "./admin/components/EnrollmentCard";
import { DevicesCard } from "./admin/components/DevicesCard";
import { useScreenBudgetAdminData } from "./admin/useScreenBudgetAdminData";
import { Label } from "@/components/ui/label";

interface ScreenBudgetAdminPageProps {
  onUnauthorized: () => void;
  currentUserId: number;
}

export function ScreenBudgetAdminPage({ onUnauthorized, currentUserId }: ScreenBudgetAdminPageProps) {
  const adminData = useScreenBudgetAdminData({ onUnauthorized, currentUserId });

  return (
    <div className="grid gap-4">
      <section className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Screen Budget Admin</h1>
          <p className="text-sm text-muted-foreground">Manage families, parent membership, device enrollment, and unenrollment.</p>
        </div>

        <div className="flex items-center gap-2">
          <Label htmlFor="screen-budget-admin-family" className="text-xs uppercase tracking-wide text-muted-foreground">
            Family
          </Label>
          <select
            id="screen-budget-admin-family"
            className="h-9 min-w-[220px] rounded-md border border-input bg-background px-2 text-sm"
            value={adminData.selectedFamilyId ?? ""}
            onChange={event => {
              const value = Number.parseInt(event.currentTarget.value, 10);
              adminData.setSelectedFamilyId(Number.isFinite(value) ? value : null);
            }}
          >
            {adminData.families.length === 0 ? <option value="">No families</option> : null}
            {adminData.families.map(family => (
              <option key={family.id} value={family.id}>
                {family.name} ({family.role})
              </option>
            ))}
          </select>
        </div>
      </section>

      {adminData.errorMessage ? <p className="text-sm text-red-600">{adminData.errorMessage}</p> : null}

      <FamilyManagementCard
        families={adminData.families}
        selectedFamilyId={adminData.selectedFamilyId}
        onSelectFamily={adminData.setSelectedFamilyId}
        createFamilyName={adminData.createFamilyName}
        onCreateFamilyNameChange={adminData.setCreateFamilyName}
        onCreateFamily={adminData.createFamily}
        isCreatingFamily={adminData.createFamilyMutation.isPending}
        deleteTargetFamilyId={adminData.deleteTargetFamilyId}
        onToggleDeleteFamily={adminData.toggleDeleteFamily}
        deleteConfirmName={adminData.deleteConfirmName}
        onDeleteConfirmNameChange={adminData.setDeleteConfirmName}
        deleteConfirmCascade={adminData.deleteConfirmCascade}
        onDeleteConfirmCascadeChange={adminData.setDeleteConfirmCascade}
        onConfirmDeleteFamily={adminData.deleteFamily}
        isDeleteMutationPending={adminData.deleteFamilyMutation.isPending}
      />

      {adminData.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading screen budget admin data...</p>
      ) : (
        <>
          <EnrollmentCard
            selectedFamilyId={adminData.selectedFamilyId}
            pairingTtlSeconds={adminData.pairingTtlSeconds}
            onPairingTtlSecondsChange={adminData.setPairingTtlSeconds}
            latestPairingCode={adminData.latestPairingCode}
            onCreatePairingCode={adminData.createPairingCode}
            isCreatingPairingCode={adminData.createPairingCodeMutation.isPending}
          />

          <ParentsCard
            selectedFamilyId={adminData.selectedFamilyId}
            selectedFamily={adminData.selectedFamily}
            currentUserId={adminData.currentUserId}
            parents={adminData.parents}
            newParentUsername={adminData.newParentUsername}
            onNewParentUsernameChange={adminData.setNewParentUsername}
            onAddParent={adminData.addParent}
            isAddingParent={adminData.addParentMutation.isPending}
            onRemoveParent={async (userId, username) => {
              const confirmed = window.confirm(`Remove parent \"${username}\" from this family?`);
              if (!confirmed) return;
              await adminData.removeParent(userId);
            }}
            isRemovingParent={adminData.removeParentMutation.isPending}
          />

          <DevicesCard
            kids={adminData.kids}
            onUnenrollKid={adminData.unenrollKid}
            onRemoveKid={async (kidId, kidName) => {
              const confirmed = window.confirm(
                `Remove device \"${kidName}\" from this family? This permanently deletes its synced data.`,
              );
              if (!confirmed) return;
              await adminData.removeKid(kidId);
            }}
            isUnenrollingKid={adminData.unenrollKidMutation.isPending}
            isRemovingKid={adminData.removeKidMutation.isPending}
          />
        </>
      )}

      {!adminData.selectedFamily && adminData.families.length > 0 ? (
        <p className="text-sm text-muted-foreground">Select a family to manage parents, devices, and enrollment.</p>
      ) : null}
    </div>
  );
}
